import supabase from '@/lib/supabaseClient';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').trim();

// Local-dev convenience only -- must never resolve to a backend URL for any
// non-localhost hostname, or a misconfigured REACT_APP_API_BASE_URL on a
// real deploy (staging or production) would silently route live traffic to
// the dev Railway backend instead of failing loudly.
function getFallbackApiBase() {
  if (typeof window === 'undefined') return '';

  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8001';
  }

  return '';
}

/**
 * File Upload Service
 * Handles secure file uploads to the backend with progress tracking
 */
export class FileUploadService {
  /**
   * Upload a file to the backend
   * @param {File} file - File object to upload
   * @param {string} companyId - Company ID for file association
   * @param {string} companyName - Company name for email notification
   * @param {Function} onProgress - Callback for upload progress (0-100)
   * @param {Object} options - Optional job linkage for storage path naming
   * @returns {Promise<Object>} Upload response with file_id, status, etc.
   */
  static async uploadFile(file, companyId, companyName, onProgress = null, options = {}) {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', companyId);
    formData.append('company_name', companyName);
    if (options.jobId) formData.append('job_id', options.jobId);
    if (options.jobReference) formData.append('job_reference', options.jobReference);
    if (options.fileCategory) formData.append('file_category', options.fileCategory);

    let apiBase = API_BASE;

    try {
      const response = await this._fetchWithProgress(
        `${apiBase}/api/wizard/file-upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        },
        onProgress
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Fallback to secondary API base if first attempt fails
      if (apiBase && !API_BASE) {
        const fallbackApiBase = getFallbackApiBase();
        if (fallbackApiBase && fallbackApiBase !== apiBase) {
          try {
            const response = await this._fetchWithProgress(
              `${fallbackApiBase}/api/wizard/file-upload`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`
                },
                body: formData
              },
              onProgress
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.detail || `Upload failed: ${response.status}`);
            }

            return await response.json();
          } catch (fallbackError) {
            throw error; // Throw original error if fallback also fails
          }
        }
      }

      throw error;
    }
  }

  /**
   * Validate file before upload
   * @param {File} file - File object to validate
   * @returns {Object} Validation result { isValid, error, warning }
   */
  static validateFile(file, category) {
    if (category === 'finished_ixbrl') {
      // Filing-only orders: the client's own finished iXBRL is XHTML, not a document to tag.
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!['.xhtml', '.html', '.htm'].includes(extension)) {
        return { isValid: false, error: 'Invalid file type. Your own iXBRL must be an XHTML file (.xhtml, .html or .htm).' };
      }
      if (file.size > 25 * 1024 * 1024) {
        return { isValid: false, error: 'An iXBRL file must be smaller than 25 MB.' };
      }
      return { isValid: true };
    }
    const ALLOWED_TYPES = {
      'application/pdf': '.pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/msword': '.doc'
    };

    const MAX_SIZE = 100 * 1024 * 1024; // 100 MB
    const WARN_SIZE = 50 * 1024 * 1024; // 50 MB

    // Check if file type is allowed
    if (!ALLOWED_TYPES[file.type]) {
      // Check by extension as fallback
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      const allowedExtensions = Object.values(ALLOWED_TYPES);
      if (!allowedExtensions.includes(extension)) {
        return {
          isValid: false,
          error: 'Invalid file type. Allowed types: PDF, Excel (.xlsx, .xls), Word (.docx, .doc)'
        };
      }
    }

    // Check file size
    if (file.size > MAX_SIZE) {
      return {
        isValid: false,
        error: `File size exceeds 100 MB limit (your file: ${(file.size / (1024 * 1024)).toFixed(2)} MB)`
      };
    }

    // Warn if large file
    if (file.size > WARN_SIZE) {
      return {
        isValid: true,
        warning: `Large file detected (${(file.size / (1024 * 1024)).toFixed(2)} MB). Upload may take longer.`
      };
    }

    return { isValid: true };
  }

  /**
   * Fetch with progress tracking for FormData uploads
   * Note: Progress tracking works by monitoring the underlying XMLHttpRequest
   * @private
   */
  static _fetchWithProgress(url, options, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress && xhr.upload) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({
            ok: true,
            status: xhr.status,
            json: () => Promise.resolve(JSON.parse(xhr.responseText))
          });
        } else {
          resolve({
            ok: false,
            status: xhr.status,
            json: () => Promise.resolve(JSON.parse(xhr.responseText || '{}'))
          });
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload canceled'));
      });

      xhr.open(options.method || 'GET', url);

      // Set headers (must be after open())
      Object.entries(options.headers || {}).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(options.body);
    });
  }

  /**
   * Get file display size (human readable)
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export default FileUploadService;
